/**
 * NetworkUndoRedoService - Servicio de undo/redo para operaciones de red
 *
 * @version 1.0
 * @created 2026-03-06
 * @module core/spatial/services
 *
 * @description
 * Implementa el patrón Command para operaciones de red con:
 * - Stack de comandos ejecutados (undo)
 * - Stack de comandos deshechos (redo)
 * - Límite de tamaño de stack (100 comandos por defecto)
 * - Integración con transacciones
 *
 * @example
 * ```typescript
 * const command = new CreateBranchCommand({
 *   projectId,
 *   nodeId: newNode.id,
 *   edgeId: newEdge.id,
 *   // ...
 * });
 *
 * await networkUndoRedoService.executeCommand(command);
 *
 * // Deshacer
 * await networkUndoRedoService.undo();
 *
 * // Rehacer
 * await networkUndoRedoService.redo();
 * ```
 */

import { canonicalNetworkApi } from './canonical-network-api.service';
import { canonicalNetworkStore } from '../stores/canonical-network-store';
import type { CanonicalNodeFeature, CanonicalEdgeFeature, CanonicalFeature } from '../stores/canonical-network-store';
import { mapEngine } from '../../map-engine/MapEngineService';
import type { GeoJSONSource } from 'maplibre-gl';

// ============================================================================
// TYPES
// ============================================================================

export type CommandType = 'create-branch' | 'split-edge' | 'split-edge-and-create-branch' | 'move-node' | 'delete-node' | 'edit-edge-geometry' | 'insert-node-on-edge' | 'merge-edges';

/**
 * Interfaz base para comandos
 */
export interface NetworkCommand {
  /** Tipo de comando */
  type: CommandType;
  /** ID único del comando */
  id: string;
  /** Timestamp de ejecución */
  executedAt: number;
  /** Ejecuta el comando */
  execute(): Promise<void>;
  /** Deshace el comando */
  undo(): Promise<void>;
  /** Descripción legible del comando */
  getDescription(): string;
}

/**
 * Snapshot reactivo del estado de undo/redo.
 *
 * @remarks
 * Se mantiene estable entre cambios para integrarse con useSyncExternalStore.
 */
export interface NetworkUndoRedoState {
  canUndo: boolean;
  canRedo: boolean;
  undoCount: number;
  redoCount: number;
  undoDescription: string | null;
  redoDescription: string | null;
  isExecuting: boolean;
}

type NetworkUndoRedoListener = () => void;

/**
 * Datos para crear rama
 *
 * @remarks
 * Almacena toda la información necesaria para REDO y UNDO de create-branch.
 * - REDO: usa intentType, sourceNodeId, targetNodeId/newNodeData, edgeData
 * - UNDO: usa undoSnapshot (fuente de verdad del backend)
 */
export interface CreateBranchCommandData {
  projectId: string;

  // ═══ Data para REDO (recrear la rama) ═══
  intentType: 'snap-node' | 'free-point';
  sourceNodeId: string;

  // Para snap-node intent
  targetNodeId?: string;

  // Para free-point intent
  newNodeData?: {
    geometry: { type: 'Point'; coordinates: [number, number] };
    sourceType?: string;
    isManualAuthority?: boolean;
  };

  // Edge data (común a ambos casos)
  edgeData: {
    geometry: { type: 'LineString'; coordinates: [number, number][] };
    roadClass: string;
    sourceType?: string;
  };

  // ═══ Snapshot del backend (fuente de verdad para UNDO) ═══
  undoSnapshot: import('../types').BranchUndoSnapshot;
}

/**
 * Datos para snap-edge (dividir edge + crear rama) - FASE 6
 * Operación compuesta de 5 pasos atómicos
 */
export interface SplitEdgeAndCreateBranchCommandData {
  projectId: string;
  sourceNodeId: string; // Nodo desde el cual se crea la rama
  sourceNodeGeometry: { type: 'Point'; coordinates: [number, number] }; // Para redo

  // Edge original (para recrear en undo)
  originalEdgeId: string;
  originalEdgeGeometry: { type: 'LineString'; coordinates: [number, number][] };
  originalFromNodeId: string;
  originalToNodeId: string;
  originalRoadClass: string;

  // Nodo split creado
  splitNodeId: string;
  splitNodeGeometry: { type: 'Point'; coordinates: [number, number] };

  // Edges creados (para eliminar en undo)
  edge1Id: string; // originalFromNode → splitNode
  edge2Id: string; // splitNode → originalToNode

  // Branch edge OPCIONAL (puede no existir si fue omitido por duplicado)
  branchEdge: {
    id: string | null;
    geometry: { type: 'LineString'; coordinates: [number, number][] } | null;
    status: 'created' | 'omitted_duplicate';
  };
}

/**
 * Datos para insertar nodo en edge (divide un edge en dos)
 * Operación de 4 pasos: crear nodo, crear edge1, crear edge2, eliminar edge original
 */
export interface InsertNodeOnEdgeCommandData {
  projectId: string;

  // Edge original (para recrear en undo)
  originalEdgeId: string;
  originalEdgeGeometry: { type: 'LineString'; coordinates: [number, number][] };
  originalFromNodeId: string;
  originalToNodeId: string;
  originalRoadClass: string | null;

  // Nodo insertado
  insertedNodeId: string;
  insertedNodeGeometry: { type: 'Point'; coordinates: [number, number] };

  // Edges creados (para eliminar en undo)
  edge1Id: string; // originalFromNode → insertedNode
  edge1Geometry: { type: 'LineString'; coordinates: [number, number][] };
  edge2Id: string; // insertedNode → originalToNode
  edge2Geometry: { type: 'LineString'; coordinates: [number, number][] };
}

/**
 * Datos para merge edges
 *
 * @remarks
 * Operación que fusiona dos edges contiguos en uno solo.
 * Undo llama unmerge, redo vuelve a llamar merge.
 */
export interface MergeEdgesCommandData {
  projectId: string;
  // IDs originales de los edges a fusionar
  edgeId1: string;
  edgeId2: string;
  // Respuesta del merge (incluye snapshot para undo)
  mergeResult: import('../types').MergeEdgesResponse;
}

// ============================================================================
// COMMANDS
// ============================================================================

/**
 * Comando para crear rama
 *
 * @remarks
 * Command con REDO y UNDO reales:
 * - execute() (REDO): Vuelve a llamar createBranch() en backend
 * - undo(): Usa undoSnapshot del backend para eliminar nodos/edges creados
 *
 * Este es un "already executed" command - NetworkEditContext lo ejecuta antes de registrarlo.
 * execute() solo se llama en REDO.
 */
export class CreateBranchCommand implements NetworkCommand {
  type: CommandType = 'create-branch';
  id: string;
  executedAt: number;

  constructor(private data: CreateBranchCommandData) {
    this.id = `cmd-create-branch-${Date.now()}`;
    this.executedAt = Date.now();
  }

  /**
   * REDO: Vuelve a crear la rama
   *
   * @remarks
   * IMPORTANTE: Este método NO se ejecuta al registrar el comando.
   * Se ejecuta SOLO cuando el usuario hace REDO después de un UNDO.
   *
   * NetworkEditContext ya ejecutó el createBranch inicial antes de registrar este comando.
   */
  async execute(): Promise<void> {
    console.log('[CreateBranchCommand] Execute (REDO):', this.id, {
      intentType: this.data.intentType,
      sourceNodeId: this.data.sourceNodeId,
    });

    try {
      // Construir DTO para createBranch
      const createBranchDto: import('../types').CreateBranchDto = {
        intentType: this.data.intentType,
        sourceNodeId: this.data.sourceNodeId,
        edge: {
          geometry: this.data.edgeData.geometry as import('../types').LineStringGeometry,
          roadClass: this.data.edgeData.roadClass as import('../types').RoadClass,
          sourceType: (this.data.edgeData.sourceType as import('../types').SourceType) || 'manual',
        },
      };

      // Agregar campos específicos según intent type
      if (this.data.intentType === 'snap-node') {
        if (!this.data.targetNodeId) {
          throw new Error('targetNodeId required for snap-node intent');
        }
        createBranchDto.targetNodeId = this.data.targetNodeId;
      } else if (this.data.intentType === 'free-point') {
        if (!this.data.newNodeData) {
          throw new Error('newNodeData required for free-point intent');
        }
        createBranchDto.newNode = {
          geometry: this.data.newNodeData.geometry as import('../types').PointGeometry,
          sourceType: (this.data.newNodeData.sourceType as import('../types').SourceType) || 'manual',
          isManualAuthority: this.data.newNodeData.isManualAuthority ?? true,
        };
      }

      // Ejecutar createBranch en backend
      const result = await canonicalNetworkApi.createBranch(this.data.projectId, createBranchDto);

      // Actualizar undoSnapshot con nuevo resultado (para próximos undos)
      this.data.undoSnapshot = result.undoSnapshot;

      // Actualizar store canónico
      const storeFeaturesForMerge: CanonicalFeature[] = [];

      // Agregar nodo al store si se creó uno nuevo
      const nodeFeature: CanonicalNodeFeature = {
        type: 'Feature',
        id: result.node.id,
        geometry: result.node.geometry,
        properties: {
          id: result.node.id,
          type: 'node',
          intersectionType: result.node.intersectionType,
          nodeVersion: result.node.nodeVersion,
        },
      };
      storeFeaturesForMerge.push(nodeFeature);

      // Agregar todos los edges al store
      for (const edge of result.edges) {
        const fromNodeId = edge.fromNode?.id || (edge as any).fromNodeId;
        const toNodeId = edge.toNode?.id || (edge as any).toNodeId;

        const edgeFeature: CanonicalEdgeFeature = {
          type: 'Feature',
          id: edge.id,
          geometry: edge.geometry,
          properties: {
            id: edge.id,
            type: 'edge',
            roadClass: edge.roadClass,
            fromNodeId,
            toNodeId,
            lengthMeters: edge.lengthMeters,
            edgeVersion: edge.edgeVersion,
          },
        };
        storeFeaturesForMerge.push(edgeFeature);
      }

      canonicalNetworkStore.merge(storeFeaturesForMerge);
      canonicalNetworkStore.syncToSource();

      console.log('[CreateBranchCommand] Execute completed:', {
        nodeId: result.node.id,
        edgesCount: result.edges.length,
      });
    } catch (error) {
      console.error('[CreateBranchCommand] Execute failed:', error);
      throw error;
    }
  }

  /**
   * UNDO: Elimina la rama creada
   *
   * @remarks
   * Usa undoSnapshot del backend como fuente de verdad.
   * Soporta múltiples edges (no solo el primero).
   */
  async undo(): Promise<void> {
    console.log('[CreateBranchCommand] Undo:', this.id, {
      createdNodeId: this.data.undoSnapshot.createdNodeId,
      createdEdgeIds: this.data.undoSnapshot.createdEdgeIds,
    });

    try {
      // 1. Eliminar TODOS los edges creados (usa snapshot del backend)
      for (const edgeId of this.data.undoSnapshot.createdEdgeIds) {
        try {
          await canonicalNetworkApi.deleteEdge(this.data.projectId, edgeId);
          console.log('[CreateBranchCommand] Edge deleted:', edgeId);
        } catch (error) {
          console.error('[CreateBranchCommand] Error deleting edge:', edgeId, error);
          throw error;
        }
      }

      // 2. Eliminar nodo del backend (solo si se creó uno nuevo en free-point)
      if (this.data.undoSnapshot.createdNodeId) {
        try {
          await canonicalNetworkApi.deleteNode(this.data.undoSnapshot.createdNodeId);
          console.log('[CreateBranchCommand] Node deleted:', this.data.undoSnapshot.createdNodeId);
        } catch (error) {
          console.error('[CreateBranchCommand] Error deleting node:', error);
          throw error;
        }
      }

      // 3. Actualizar store canónico
      // Remover todos los edges
      canonicalNetworkStore.remove(this.data.undoSnapshot.createdEdgeIds);

      // Remover nodo si fue creado
      if (this.data.undoSnapshot.createdNodeId) {
        canonicalNetworkStore.remove([this.data.undoSnapshot.createdNodeId]);
      }

      // Sincronizar con source del mapa
      canonicalNetworkStore.syncToSource();

      console.log('[CreateBranchCommand] Undo completed - removed:', {
        edgesCount: this.data.undoSnapshot.createdEdgeIds.length,
        nodeRemoved: !!this.data.undoSnapshot.createdNodeId,
      });
    } catch (error) {
      console.error('[CreateBranchCommand] Undo failed:', error);
      throw error;
    }
  }

  getDescription(): string {
    return 'Crear rama';
  }
}

/**
 * Comando para operación compuesta: dividir edge + crear rama (FASE 6)
 *
 * @remarks
 * Esta operación compuesta realiza 5 pasos atómicos:
 * 1. Crear nodo split
 * 2. Eliminar edge original
 * 3. Crear edge1 (fromNode → split)
 * 4. Crear edge2 (split → toNode)
 * 5. Crear branchEdge (sourceNode → split)
 *
 * El undo debe revertir TODA la operación como una unidad atómica.
 */
export class SplitEdgeAndCreateBranchCommand implements NetworkCommand {
  type: CommandType = 'split-edge-and-create-branch';
  id: string;
  executedAt: number;

  constructor(private data: SplitEdgeAndCreateBranchCommandData) {
    this.id = `cmd-split-edge-branch-${Date.now()}`;
    this.executedAt = Date.now();
  }

  async execute(): Promise<void> {
    console.log('[SplitEdgeAndCreateBranchCommand] Execute:', this.id);

    // La operación ya se ejecutó en confirmBranchCreation
    // Este método se usa solo para REDO

    // 1. Crear nodo split
    const splitNode = await canonicalNetworkApi.createNode(this.data.projectId, {
      geometry: this.data.splitNodeGeometry,
      sourceType: 'manual',
      intersectionType: 'simple', // Nodo split es un simple
      isManualAuthority: true,
    });

    // 2. Eliminar edge original
    await canonicalNetworkApi.deleteEdge(this.data.projectId, this.data.originalEdgeId);

    // 3. Crear edge1 (fromNode → split)
    const edge1 = await canonicalNetworkApi.createEdge(this.data.projectId, {
      fromNodeId: this.data.originalFromNodeId,
      toNodeId: splitNode.id,
      geometry: {
        type: 'LineString',
        coordinates: [
          this.data.originalEdgeGeometry.coordinates[0],
          this.data.splitNodeGeometry.coordinates,
        ],
      },
      sourceType: 'manual',
      roadClass: (this.data.originalRoadClass || 'residential') as 'motorway' | 'trunk' | 'primary' | 'secondary' | 'tertiary' | 'residential' | 'service' | 'unclassified' | 'pedestrian' | 'cycleway',
    });

    // 4. Crear edge2 (split → toNode)
    const edge2 = await canonicalNetworkApi.createEdge(this.data.projectId, {
      fromNodeId: splitNode.id,
      toNodeId: this.data.originalToNodeId,
      geometry: {
        type: 'LineString',
        coordinates: [
          this.data.splitNodeGeometry.coordinates,
          this.data.originalEdgeGeometry.coordinates[this.data.originalEdgeGeometry.coordinates.length - 1],
        ],
      },
      sourceType: 'manual',
      roadClass: (this.data.originalRoadClass || 'residential') as 'motorway' | 'trunk' | 'primary' | 'secondary' | 'tertiary' | 'residential' | 'service' | 'unclassified' | 'pedestrian' | 'cycleway',
    });

    // 5. Crear branchEdge SOLO si fue creado originalmente (sourceNode → split)
    let branchEdge: import('../types').CanonicalEdge | null = null;
    if (this.data.branchEdge.status === 'created' && this.data.branchEdge.geometry) {
      branchEdge = await canonicalNetworkApi.createEdge(this.data.projectId, {
        fromNodeId: this.data.sourceNodeId,
        toNodeId: splitNode.id,
        geometry: this.data.branchEdge.geometry,
        sourceType: 'manual',
        roadClass: 'residential' as const,
      });
      console.log('[SplitEdgeAndCreateBranchCommand] Branch edge recreated:', branchEdge.id);
    } else {
      console.log('[SplitEdgeAndCreateBranchCommand] Branch edge was omitted - skip recreation');
    }

    console.log('[SplitEdgeAndCreateBranchCommand] Execute completed:', {
      splitNode: splitNode.id,
      edge1: edge1.id,
      edge2: edge2.id,
      branchEdge: branchEdge?.id || 'omitted',
    });

    // Actualizar mapa
    this.updateMapAfterExecute(splitNode, edge1, edge2, branchEdge);
  }

  async undo(): Promise<void> {
    console.log('[SplitEdgeAndCreateBranchCommand] Undo:', this.id);

    // Revertir en orden inverso:

    // 5. Eliminar branchEdge SOLO si fue creado
    if (this.data.branchEdge.status === 'created' && this.data.branchEdge.id) {
      await canonicalNetworkApi.deleteEdge(this.data.projectId, this.data.branchEdge.id);
      console.log('[SplitEdgeAndCreateBranchCommand] Branch edge deleted:', this.data.branchEdge.id);
    } else {
      console.log('[SplitEdgeAndCreateBranchCommand] Branch edge was omitted - skip deletion');
    }

    // 4. Eliminar edge2
    await canonicalNetworkApi.deleteEdge(this.data.projectId, this.data.edge2Id);
    console.log('[SplitEdgeAndCreateBranchCommand] Edge2 deleted:', this.data.edge2Id);

    // 3. Eliminar edge1
    await canonicalNetworkApi.deleteEdge(this.data.projectId, this.data.edge1Id);
    console.log('[SplitEdgeAndCreateBranchCommand] Edge1 deleted:', this.data.edge1Id);

    // 2. Recrear edge original
    const originalEdge = await canonicalNetworkApi.createEdge(this.data.projectId, {
      fromNodeId: this.data.originalFromNodeId,
      toNodeId: this.data.originalToNodeId,
      geometry: this.data.originalEdgeGeometry,
      sourceType: 'manual',
      roadClass: (this.data.originalRoadClass || 'residential') as 'motorway' | 'trunk' | 'primary' | 'secondary' | 'tertiary' | 'residential' | 'service' | 'unclassified' | 'pedestrian' | 'cycleway',
    });
    console.log('[SplitEdgeAndCreateBranchCommand] Original edge recreated:', originalEdge.id);

    // 1. Eliminar nodo split
    await canonicalNetworkApi.deleteNode(this.data.splitNodeId);
    console.log('[SplitEdgeAndCreateBranchCommand] Split node deleted:', this.data.splitNodeId);

    // Actualizar mapa
    this.updateMapAfterUndo(originalEdge);
  }

  private updateMapAfterExecute(
    splitNode: import('../types').CanonicalNode,
    edge1: import('../types').CanonicalEdge,
    edge2: import('../types').CanonicalEdge,
    branchEdge: import('../types').CanonicalEdge | null
  ): void {
    const map = mapEngine.getMap();
    if (!map) return;

    const source = map.getSource('canonical-network') as GeoJSONSource;
    if (!source) return;

    const currentData = (source as any)._data as GeoJSON.FeatureCollection;
    if (!currentData || !currentData.features) return;

    // Remover edge original
    let newFeatures = currentData.features.filter(
      (f) => (f.properties?.id || f.id) !== this.data.originalEdgeId
    );

    // Agregar nuevas features
    const newFeaturesArray: GeoJSON.Feature[] = [
      {
        type: 'Feature',
        id: splitNode.id,
        geometry: splitNode.geometry,
        properties: {
          id: splitNode.id,
          type: 'node',
          sourceType: splitNode.sourceType,
          intersectionType: splitNode.intersectionType,
        },
      } as GeoJSON.Feature,
      {
        type: 'Feature',
        id: edge1.id,
        geometry: edge1.geometry,
        properties: {
          id: edge1.id,
          type: 'edge',
          fromNodeId: edge1.fromNode.id,
          toNodeId: edge1.toNode.id,
          roadClass: edge1.roadClass,
          lengthMeters: edge1.lengthMeters,
        },
      } as GeoJSON.Feature,
      {
        type: 'Feature',
        id: edge2.id,
        geometry: edge2.geometry,
        properties: {
          id: edge2.id,
          type: 'edge',
          fromNodeId: edge2.fromNode.id,
          toNodeId: edge2.toNode.id,
          roadClass: edge2.roadClass,
          lengthMeters: edge2.lengthMeters,
        },
      } as GeoJSON.Feature,
    ];

    // Agregar branchEdge solo si existe
    if (branchEdge) {
      newFeaturesArray.push({
        type: 'Feature',
        id: branchEdge.id,
        geometry: branchEdge.geometry,
        properties: {
          id: branchEdge.id,
          type: 'edge',
          fromNodeId: branchEdge.fromNode.id,
          toNodeId: branchEdge.toNode.id,
          roadClass: branchEdge.roadClass,
          lengthMeters: branchEdge.lengthMeters,
        },
      } as GeoJSON.Feature);
    }

    newFeatures = [...newFeatures, ...newFeaturesArray];

    source.setData({
      type: 'FeatureCollection',
      features: newFeatures,
    });

    console.log('[SplitEdgeAndCreateBranchCommand] Map updated after execute');
  }

  private updateMapAfterUndo(originalEdge: any): void {
    const map = mapEngine.getMap();
    if (!map) return;

    const source = map.getSource('canonical-network') as GeoJSONSource;
    if (!source) return;

    const currentData = (source as any)._data as GeoJSON.FeatureCollection;
    if (!currentData || !currentData.features) return;

    // Remover todas las features creadas
    let newFeatures = currentData.features.filter((f) => {
      const featureId = f.properties?.id || f.id;
      return (
        featureId !== this.data.splitNodeId &&
        featureId !== this.data.edge1Id &&
        featureId !== this.data.edge2Id &&
        featureId !== this.data.branchEdge.id
      );
    });

    // Agregar edge original
    newFeatures = [
      ...newFeatures,
      {
        type: 'Feature',
        id: originalEdge.id,
        geometry: originalEdge.geometry,
        properties: {
          id: originalEdge.id,
          type: 'edge',
          fromNodeId: originalEdge.fromNode.id,
          toNodeId: originalEdge.toNode.id,
          roadClass: originalEdge.roadClass,
          lengthMeters: originalEdge.lengthMeters,
        },
      } as GeoJSON.Feature,
    ];

    source.setData({
      type: 'FeatureCollection',
      features: newFeatures,
    });

    console.log('[SplitEdgeAndCreateBranchCommand] Map updated after undo');
  }

  getDescription(): string {
    return 'Dividir segmento y crear rama';
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// MOVE NODE COMMAND (BUG 2 FIX)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Snapshot completo del estado de un nodo y sus edges
 * Almacena features completas para restaurar exactamente el estado
 */
export interface MoveNodeStateSnapshot {
  nodeFeature: CanonicalNodeFeature;
  edgeFeatures: CanonicalEdgeFeature[];
}

/**
 * Datos para comando de mover nodo
 * Usa snapshots completos de features para undo/redo perfecto
 */
export interface MoveNodeCommandData {
  projectId: string;
  nodeId: string;
  beforeState: MoveNodeStateSnapshot;
  afterState: MoveNodeStateSnapshot;
}

/**
 * Comando para mover un nodo con undo/redo
 *
 * @remarks
 * Este comando trabaja con snapshots COMPLETOS de las features afectadas.
 * No reconstruye features parciales - usa las features exactas guardadas.
 *
 * IMPORTANTE: Usa updateNode + updateEdge individualmente (NO batchMoveNode que no existe).
 * Esto coincide exactamente con el flujo de confirmNodeMove en NetworkEditContext.
 *
 * Undo: Restaura beforeState (features completas con geometrías originales)
 * Redo: Restaura afterState (features completas desde respuesta del backend)
 */
export class MoveNodeCommand implements NetworkCommand {
  type: CommandType = 'move-node';
  id: string;
  executedAt: number;

  constructor(private data: MoveNodeCommandData) {
    this.id = `cmd-move-node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.executedAt = Date.now();
  }

  /**
   * Execute (usado para REDO)
   * Restaura el afterState usando updateNode + updateEdge (endpoints existentes)
   */
  async execute(): Promise<void> {
    console.log('[MoveNodeCommand] Execute (redo):', this.id);

    try {
      await this.applyState(this.data.afterState);
      console.log('[MoveNodeCommand] Execute (redo) completed');
    } catch (error) {
      console.error('[MoveNodeCommand] Execute failed:', error);
      throw error;
    }
  }

  /**
   * Undo: Restaura el beforeState
   * Usa updateNode + updateEdge (endpoints existentes)
   */
  async undo(): Promise<void> {
    console.log('[MoveNodeCommand] Undo:', this.id);

    try {
      await this.applyState(this.data.beforeState);
      console.log('[MoveNodeCommand] Undo completed');
    } catch (error) {
      console.error('[MoveNodeCommand] Undo failed:', error);
      throw error;
    }
  }

  /**
   * Aplica un estado (beforeState o afterState) al backend y al store
   * Usa los mismos endpoints que confirmNodeMove: updateNode + updateEdge
   */
  private async applyState(state: MoveNodeStateSnapshot): Promise<void> {
    console.log('[MoveNodeCommand] Applying state:', {
      nodeId: state.nodeFeature.properties.id,
      nodePosition: state.nodeFeature.geometry.coordinates,
      edgesCount: state.edgeFeatures.length,
    });

    // 1. Actualizar geometría del nodo (mismo endpoint que confirmNodeMove)
    await canonicalNetworkApi.updateNode(this.data.nodeId, {
      geometry: state.nodeFeature.geometry as {
        type: 'Point';
        coordinates: [number, number];
      },
    });

    console.log('[MoveNodeCommand] Node geometry updated:', this.data.nodeId);

    // 2. Actualizar geometría de cada edge (mismo endpoint que confirmNodeMove)
    for (const edgeFeature of state.edgeFeatures) {
      await canonicalNetworkApi.updateEdge(edgeFeature.properties.id, {
        geometry: edgeFeature.geometry as {
          type: 'LineString';
          coordinates: Array<[number, number]>;
        },
      });

      console.log('[MoveNodeCommand] Edge geometry updated:', edgeFeature.properties.id);
    }

    // 3. Actualizar store con el estado aplicado
    this.updateStoreWithState(state);
  }

  /**
   * Actualiza el store con un snapshot de estado completo
   * Usa las features guardadas directamente, sin reconstruirlas
   */
  private updateStoreWithState(state: MoveNodeStateSnapshot): void {
    const features = [state.nodeFeature, ...state.edgeFeatures];

    // updateAndSync hace merge + syncToSource
    canonicalNetworkStore.updateAndSync(features);

    console.log('[MoveNodeCommand] Store updated with state:', {
      nodeId: state.nodeFeature.properties.id,
      edgesCount: state.edgeFeatures.length,
    });
  }

  getDescription(): string {
    return 'Mover nodo';
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// EDIT EDGE GEOMETRY COMMAND (Edición geométrica de segmento)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Datos para comando de edición geométrica de edge
 * Guarda geometría completa before/after para undo/redo perfecto
 */
export interface EditEdgeGeometryCommandData {
  projectId: string;
  edgeId: string;
  beforeGeometry: {
    type: 'LineString';
    coordinates: Array<[number, number]>;
  };
  afterGeometry: {
    type: 'LineString';
    coordinates: Array<[number, number]>;
  };
}

/**
 * Comando para editar la geometría interna de un edge (shape points)
 *
 * @remarks
 * Este comando modifica SOLO la geometría del edge, NO su topología.
 * - No afecta fromNodeId/toNodeId
 * - No afecta conectividad
 * - No afecta sintaxis espacial
 * - Solo modifica coordinates[1..n-2] (vértices internos)
 *
 * Usa updateEdge (NO updateNode) para persistir cambios.
 */
export class EditEdgeGeometryCommand implements NetworkCommand {
  type: CommandType = 'edit-edge-geometry';
  id: string;
  executedAt: number;

  constructor(private data: EditEdgeGeometryCommandData) {
    this.id = `cmd-edit-edge-geom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.executedAt = Date.now();
  }

  /**
   * Execute (usado para REDO)
   * Restaura afterGeometry
   */
  async execute(): Promise<void> {
    console.log('[EditEdgeGeometryCommand] Execute (redo):', this.id);

    try {
      await this.applyGeometry(this.data.afterGeometry);
      console.log('[EditEdgeGeometryCommand] Execute completed');
    } catch (error) {
      console.error('[EditEdgeGeometryCommand] Execute failed:', error);
      throw error;
    }
  }

  /**
   * Undo: Restaura beforeGeometry
   */
  async undo(): Promise<void> {
    console.log('[EditEdgeGeometryCommand] Undo:', this.id);

    try {
      await this.applyGeometry(this.data.beforeGeometry);
      console.log('[EditEdgeGeometryCommand] Undo completed');
    } catch (error) {
      console.error('[EditEdgeGeometryCommand] Undo failed:', error);
      throw error;
    }
  }

  /**
   * Aplica una geometría al edge (backend + store + handles si están activos)
   */
  private async applyGeometry(geometry: { type: 'LineString'; coordinates: Array<[number, number]> }): Promise<void> {
    console.log('[EditEdgeGeometryCommand] Applying geometry:', {
      edgeId: this.data.edgeId,
      vertexCount: geometry.coordinates.length,
    });

    // 1. Actualizar en backend (mismo endpoint que confirmNodeMove usa para edges)
    const updatedEdge = await canonicalNetworkApi.updateEdge(this.data.edgeId, {
      geometry,
    });

    // 2. Preparar feature para el store
    const edgeFeature: CanonicalEdgeFeature = {
      type: 'Feature',
      geometry: updatedEdge.geometry,
      properties: {
        id: updatedEdge.id,
        type: 'edge',
        roadClass: updatedEdge.roadClass,
        fromNodeId: updatedEdge.fromNode.id,
        toNodeId: updatedEdge.toNode.id,
        lengthMeters: updatedEdge.lengthMeters,
        edgeVersion: (updatedEdge as any).edgeVersion || 1,
      },
    };

    // 3. CRÍTICO: Batch ambas actualizaciones visuales en el mismo frame de render
    // Esto evita el delay visual entre línea y handles
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        // Actualizar store (línea) - esto llama setData en canonical-network
        canonicalNetworkStore.updateAndSync([edgeFeature]);

        // Actualizar handles - esto llama setData en edge-shape-handles
        this.syncShapeHandles(geometry.coordinates);

        console.log('[EditEdgeGeometryCommand] ✅ Batched visual update complete');
        resolve();
      });
    });

    console.log('[EditEdgeGeometryCommand] Edge updated:', this.data.edgeId);
  }

  /**
   * Sincroniza los handles de shape points si el source existe (geometry edit activo)
   * Llamado sincrónicamente después de actualizar el store para evitar delay visual
   *
   * IMPORTANTE: Debe coincidir exactamente con useEdgeShapeHandlesLayer:
   * - Solo vértices internos (excluyendo endpoints)
   * - Properties: edgeId, shapePointIndex, isShapePoint: true
   */
  private syncShapeHandles(coordinates: Array<[number, number]>): void {
    const map = mapEngine.getMap();
    if (!map) return;

    const handlesSource = map.getSource('edge-shape-handles') as GeoJSONSource;
    if (!handlesSource) {
      // No estamos en geometry edit mode, nada que hacer
      return;
    }

    const edgeId = this.data.edgeId;
    const handleFeatures: GeoJSON.Feature<GeoJSON.Point>[] = [];

    // IMPORTANTE: Igual que useEdgeShapeHandlesLayer - solo vértices internos (1 to length-2)
    // Los endpoints (0 y length-1) son nodos topológicos, NO shape points editables
    for (let i = 1; i < coordinates.length - 1; i++) {
      handleFeatures.push({
        type: 'Feature',
        id: `${edgeId}-sp-${i}`,
        geometry: {
          type: 'Point',
          coordinates: coordinates[i],
        },
        properties: {
          edgeId,
          shapePointIndex: i,
          isShapePoint: true,
        },
      });
    }

    handlesSource.setData({
      type: 'FeatureCollection',
      features: handleFeatures,
    });

    console.log('[EditEdgeGeometryCommand] 🔄 Shape handles synced:', handleFeatures.length, 'internal vertices for edgeId:', edgeId);
  }

  getDescription(): string {
    return 'Editar geometría de segmento';
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// INSERT NODE ON EDGE COMMAND
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Comando para insertar nodo en un edge (divide el edge en dos)
 *
 * @remarks
 * Esta operación:
 * 1. Crea un nodo en el punto de división
 * 2. Crea edge1: fromNode → insertedNode
 * 3. Crea edge2: insertedNode → toNode
 * 4. Elimina el edge original
 *
 * Undo: Elimina los nuevos edges y nodo, recrea el edge original
 * Redo: Re-ejecuta la división
 */
export class InsertNodeOnEdgeCommand implements NetworkCommand {
  type: CommandType = 'insert-node-on-edge';
  id: string;
  executedAt: number;

  constructor(private data: InsertNodeOnEdgeCommandData) {
    this.id = `cmd-insert-node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.executedAt = Date.now();
  }

  /**
   * Execute (usado para REDO)
   */
  async execute(): Promise<void> {
    console.log('[InsertNodeOnEdgeCommand] Execute (redo):', this.id);

    try {
      // 1. Crear nodo
      const node = await canonicalNetworkApi.createNode(this.data.projectId, {
        geometry: this.data.insertedNodeGeometry,
        sourceType: 'manual',
        isManualAuthority: true,
      });

      // 2. Crear edge1
      const edge1 = await canonicalNetworkApi.createEdge(this.data.projectId, {
        fromNodeId: this.data.originalFromNodeId,
        toNodeId: node.id,
        geometry: this.data.edge1Geometry,
        sourceType: 'manual',
        roadClass: (this.data.originalRoadClass || 'residential') as any,
      });

      // 3. Crear edge2
      const edge2 = await canonicalNetworkApi.createEdge(this.data.projectId, {
        fromNodeId: node.id,
        toNodeId: this.data.originalToNodeId,
        geometry: this.data.edge2Geometry,
        sourceType: 'manual',
        roadClass: (this.data.originalRoadClass || 'residential') as any,
      });

      // 4. Eliminar edge original
      await canonicalNetworkApi.deleteEdge(this.data.projectId, this.data.originalEdgeId);

      // 5. Actualizar store
      canonicalNetworkStore.remove([this.data.originalEdgeId]);

      const nodeFeature: CanonicalNodeFeature = {
        type: 'Feature',
        id: node.id,
        geometry: node.geometry,
        properties: { id: node.id, type: 'node', intersectionType: 'simple' },
      };

      const edge1Feature: CanonicalEdgeFeature = {
        type: 'Feature',
        id: edge1.id,
        geometry: edge1.geometry,
        properties: {
          id: edge1.id,
          type: 'edge',
          fromNodeId: edge1.fromNode.id,
          toNodeId: edge1.toNode.id,
          roadClass: edge1.roadClass,
          lengthMeters: edge1.lengthMeters,
        },
      };

      const edge2Feature: CanonicalEdgeFeature = {
        type: 'Feature',
        id: edge2.id,
        geometry: edge2.geometry,
        properties: {
          id: edge2.id,
          type: 'edge',
          fromNodeId: edge2.fromNode.id,
          toNodeId: edge2.toNode.id,
          roadClass: edge2.roadClass,
          lengthMeters: edge2.lengthMeters,
        },
      };

      canonicalNetworkStore.updateAndSync([nodeFeature, edge1Feature, edge2Feature]);

      // Actualizar IDs para futuros undos
      this.data.insertedNodeId = node.id;
      this.data.edge1Id = edge1.id;
      this.data.edge2Id = edge2.id;

      console.log('[InsertNodeOnEdgeCommand] Execute completed');
    } catch (error) {
      console.error('[InsertNodeOnEdgeCommand] Execute failed:', error);
      throw error;
    }
  }

  /**
   * Undo: Elimina los elementos creados y restaura el edge original
   */
  async undo(): Promise<void> {
    console.log('[InsertNodeOnEdgeCommand] Undo:', this.id);

    try {
      // 1. Eliminar edge2
      await canonicalNetworkApi.deleteEdge(this.data.projectId, this.data.edge2Id);
      console.log('[InsertNodeOnEdgeCommand] Edge2 deleted:', this.data.edge2Id);

      // 2. Eliminar edge1
      await canonicalNetworkApi.deleteEdge(this.data.projectId, this.data.edge1Id);
      console.log('[InsertNodeOnEdgeCommand] Edge1 deleted:', this.data.edge1Id);

      // 3. Eliminar nodo insertado
      await canonicalNetworkApi.deleteNode(this.data.insertedNodeId);
      console.log('[InsertNodeOnEdgeCommand] Node deleted:', this.data.insertedNodeId);

      // 4. Recrear edge original
      const originalEdge = await canonicalNetworkApi.createEdge(this.data.projectId, {
        fromNodeId: this.data.originalFromNodeId,
        toNodeId: this.data.originalToNodeId,
        geometry: this.data.originalEdgeGeometry,
        sourceType: 'manual',
        roadClass: (this.data.originalRoadClass || 'residential') as any,
      });
      console.log('[InsertNodeOnEdgeCommand] Original edge recreated:', originalEdge.id);

      // 5. Actualizar store
      canonicalNetworkStore.remove([this.data.insertedNodeId, this.data.edge1Id, this.data.edge2Id]);

      const originalEdgeFeature: CanonicalEdgeFeature = {
        type: 'Feature',
        id: originalEdge.id,
        geometry: originalEdge.geometry,
        properties: {
          id: originalEdge.id,
          type: 'edge',
          fromNodeId: originalEdge.fromNode.id,
          toNodeId: originalEdge.toNode.id,
          roadClass: originalEdge.roadClass,
          lengthMeters: originalEdge.lengthMeters,
        },
      };

      canonicalNetworkStore.updateAndSync([originalEdgeFeature]);

      // Actualizar ID del edge original para futuros redos
      this.data.originalEdgeId = originalEdge.id;

      console.log('[InsertNodeOnEdgeCommand] Undo completed');
    } catch (error) {
      console.error('[InsertNodeOnEdgeCommand] Undo failed:', error);
      throw error;
    }
  }

  getDescription(): string {
    return 'Insertar nodo en segmento';
  }
}

/**
 * Comando para fusionar dos edges contiguos
 *
 * @remarks
 * Este comando implementa la operación "Unir segmentos" usando el patrón "already executed command".
 *
 * Flujo de ejecución:
 * 1. NetworkEditContext ejecuta el merge directamente (ejecución inicial)
 * 2. Crea este command con el resultado del merge
 * 3. Registra el command usando registerCommand() (NO executeCommand)
 * 4. execute() se usa SOLO para REDO (si se deshace y luego rehace)
 *
 * Métodos:
 * - execute(): lógica de REDO - vuelve a llamar al endpoint merge
 * - undo(): lógica de UNDO - llama al endpoint unmerge con snapshot
 *
 * El comando guarda el resultado completo del merge (incluido snapshot)
 * para poder revertir y rehacer la operación atómicamente.
 */
export class MergeEdgesCommand implements NetworkCommand {
  type: CommandType = 'merge-edges';
  id: string;
  executedAt: number;

  constructor(private data: MergeEdgesCommandData) {
    this.id = `cmd-merge-edges-${Date.now()}`;
    this.executedAt = Date.now();
  }

  /**
   * REDO: Vuelve a ejecutar el merge
   *
   * @remarks
   * IMPORTANTE: Este método NO se ejecuta al registrar el comando.
   * Se ejecuta SOLO cuando el usuario hace REDO después de un UNDO.
   *
   * NetworkEditContext ya ejecutó el merge inicial antes de registrar este comando.
   */
  async execute(): Promise<void> {
    console.log('[MergeEdgesCommand] Execute (REDO):', this.id);

    try {
      // Ejecutar merge en backend
      const result = await canonicalNetworkApi.mergeEdges(
        this.data.projectId,
        this.data.edgeId1,
        this.data.edgeId2
      );

      // Actualizar datos con nuevo resultado (para próximos undos)
      this.data.mergeResult = result;

      // Actualizar store canónico: eliminar edges viejos + nodo
      canonicalNetworkStore.remove(this.data.mergeResult.deletedEdgeIds);
      canonicalNetworkStore.remove([this.data.mergeResult.deletedNodeId]);

      // Agregar merged edge al store
      const mergedEdgeFeature: CanonicalEdgeFeature = {
        type: 'Feature',
        id: result.mergedEdge.id,
        geometry: result.mergedEdge.geometry,
        properties: {
          id: result.mergedEdge.id,
          type: 'edge',
          fromNodeId: result.mergedEdge.fromNodeId,
          toNodeId: result.mergedEdge.toNodeId,
          lengthMeters: result.mergedEdge.lengthMeters,
          roadClass: result.mergedEdge.roadClass,
        },
      };

      canonicalNetworkStore.merge([mergedEdgeFeature]);
      canonicalNetworkStore.syncToSource();

      console.log('[MergeEdgesCommand] Execute completed - merged edge:', result.mergedEdge.id);
    } catch (error) {
      console.error('[MergeEdgesCommand] Execute failed:', error);
      throw error;
    }
  }

  async undo(): Promise<void> {
    console.log('[MergeEdgesCommand] Undo:', this.id);

    try {
      // Llamar unmerge con snapshot
      const result = await canonicalNetworkApi.unmergeEdge(
        this.data.projectId,
        this.data.mergeResult.mergedEdge.id,
        this.data.mergeResult.snapshot
      );

      // Actualizar store canónico
      // 1. Eliminar merged edge
      canonicalNetworkStore.remove([this.data.mergeResult.mergedEdge.id]);

      // 2. Agregar edges restaurados
      const restoredEdge1Feature: CanonicalEdgeFeature = {
        type: 'Feature',
        id: result.restoredEdge1.id,
        geometry: result.restoredEdge1.geometry,
        properties: {
          id: result.restoredEdge1.id,
          type: 'edge',
          fromNodeId: result.restoredEdge1.fromNodeId,
          toNodeId: result.restoredEdge1.toNodeId,
        },
      };

      const restoredEdge2Feature: CanonicalEdgeFeature = {
        type: 'Feature',
        id: result.restoredEdge2.id,
        geometry: result.restoredEdge2.geometry,
        properties: {
          id: result.restoredEdge2.id,
          type: 'edge',
          fromNodeId: result.restoredEdge2.fromNodeId,
          toNodeId: result.restoredEdge2.toNodeId,
        },
      };

      const restoredNodeFeature: CanonicalNodeFeature = {
        type: 'Feature',
        id: result.restoredNode.id,
        geometry: result.restoredNode.geometry,
        properties: {
          id: result.restoredNode.id,
          type: 'node',
        },
      };

      canonicalNetworkStore.merge([restoredEdge1Feature, restoredEdge2Feature, restoredNodeFeature]);

      // 3. Sincronizar con MapLibre
      canonicalNetworkStore.syncToSource();

      console.log('[MergeEdgesCommand] Undo completed - restored edges:', [
        result.restoredEdge1.id,
        result.restoredEdge2.id,
      ]);
    } catch (error) {
      console.error('[MergeEdgesCommand] Undo failed:', error);
      throw error;
    }
  }

  getDescription(): string {
    return 'Unir segmentos';
  }
}

// ============================================================================
// SERVICE
// ============================================================================

class NetworkUndoRedoService {
  private undoStack: NetworkCommand[] = [];
  private redoStack: NetworkCommand[] = [];
  private maxStackSize = 100;
  private isExecuting = false;
  private listeners: Set<NetworkUndoRedoListener> = new Set();
  private snapshot: NetworkUndoRedoState = {
    canUndo: false,
    canRedo: false,
    undoCount: 0,
    redoCount: 0,
    undoDescription: null,
    redoDescription: null,
    isExecuting: false,
  };

  /**
   * Suscribe listeners a cambios del stack para UI reactiva.
   */
  subscribe(listener: NetworkUndoRedoListener): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private publishState(): void {
    const prevSnapshot = this.snapshot;
    this.snapshot = {
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      undoCount: this.undoStack.length,
      redoCount: this.redoStack.length,
      undoDescription: this.getUndoDescription(),
      redoDescription: this.getRedoDescription(),
      isExecuting: this.isExecuting,
    };

    console.log('[NetworkUndoRedoService] 📢 publishState:', {
      prevCanUndo: prevSnapshot.canUndo,
      newCanUndo: this.snapshot.canUndo,
      undoCount: this.snapshot.undoCount,
      listenerCount: this.listeners.size,
    });

    this.notifyListeners();
  }

  /**
   * Ejecuta un comando y lo agrega al stack de undo
   */
  async executeCommand(command: NetworkCommand): Promise<void> {
    if (this.isExecuting) {
      console.warn('[NetworkUndoRedoService] Already executing a command');
      return;
    }

    try {
      this.isExecuting = true;
      this.publishState();

      await command.execute();

      // Agregar al stack de undo
      this.undoStack.push(command);

      // Limpiar stack de redo (no se puede rehacer después de un nuevo comando)
      this.redoStack = [];

      // Limitar tamaño del stack
      if (this.undoStack.length > this.maxStackSize) {
        this.undoStack.shift();
      }

      console.log('[NetworkUndoRedoService] Command executed:', command.id, {
        undoStackSize: this.undoStack.length,
        redoStackSize: this.redoStack.length,
      });
    } finally {
      this.isExecuting = false;
      this.publishState();
    }
  }

  /**
   * Registra un comando ya ejecutado (para operaciones que se ejecutan fuera del servicio)
   */
  registerCommand(command: NetworkCommand): void {
    this.undoStack.push(command);
    this.redoStack = [];

    if (this.undoStack.length > this.maxStackSize) {
      this.undoStack.shift();
    }

    console.log('[NetworkUndoRedoService] Command registered:', command.id, {
      undoStackSize: this.undoStack.length,
      canUndo: this.canUndo(),
      listenerCount: this.listeners.size,
    });
    this.publishState();
  }

  /**
   * Deshace el último comando
   */
  async undo(): Promise<void> {
    if (this.undoStack.length === 0) {
      console.warn('[NetworkUndoRedoService] Nothing to undo');
      return;
    }

    if (this.isExecuting) {
      console.warn('[NetworkUndoRedoService] Already executing a command');
      return;
    }

    // IMPORTANTE: Guardamos el comando ANTES de sacarlo del stack
    // Si el undo falla, lo devolvemos al stack para no perderlo
    const command = this.undoStack[this.undoStack.length - 1];

    try {
      this.isExecuting = true;
      this.publishState();

      console.log('[NetworkUndoRedoService] Undoing command:', command.id);

      await command.undo();

      // Solo si el undo fue exitoso, movemos el comando
      this.undoStack.pop();
      this.redoStack.push(command);

      console.log('[NetworkUndoRedoService] Command undone:', command.id, {
        undoStackSize: this.undoStack.length,
        redoStackSize: this.redoStack.length,
      });
    } catch (error) {
      // El comando permanece en undoStack - no se pierde
      console.error('[NetworkUndoRedoService] Error undoing command (command preserved in stack):', error);
      throw error;
    } finally {
      this.isExecuting = false;
      this.publishState();
    }
  }

  /**
   * Rehace el último comando deshecho
   */
  async redo(): Promise<void> {
    if (this.redoStack.length === 0) {
      console.warn('[NetworkUndoRedoService] Nothing to redo');
      return;
    }

    if (this.isExecuting) {
      console.warn('[NetworkUndoRedoService] Already executing a command');
      return;
    }

    // IMPORTANTE: Guardamos el comando ANTES de sacarlo del stack
    // Si el redo falla, lo devolvemos al stack para no perderlo
    const command = this.redoStack[this.redoStack.length - 1];

    try {
      this.isExecuting = true;
      this.publishState();

      console.log('[NetworkUndoRedoService] Redoing command:', command.id);

      await command.execute();

      // Solo si el redo fue exitoso, movemos el comando
      this.redoStack.pop();
      this.undoStack.push(command);

      console.log('[NetworkUndoRedoService] Command redone:', command.id, {
        undoStackSize: this.undoStack.length,
        redoStackSize: this.redoStack.length,
      });
    } catch (error) {
      // El comando permanece en redoStack - no se pierde
      console.error('[NetworkUndoRedoService] Error redoing command (command preserved in stack):', error);
      throw error;
    } finally {
      this.isExecuting = false;
      this.publishState();
    }
  }

  /**
   * Verifica si se puede deshacer
   */
  canUndo(): boolean {
    return this.undoStack.length > 0 && !this.isExecuting;
  }

  /**
   * Verifica si se puede rehacer
   */
  canRedo(): boolean {
    return this.redoStack.length > 0 && !this.isExecuting;
  }

  /**
   * Obtiene la descripción del próximo comando a deshacer
   */
  getUndoDescription(): string | null {
    if (this.undoStack.length === 0) return null;
    return this.undoStack[this.undoStack.length - 1].getDescription();
  }

  /**
   * Obtiene la descripción del próximo comando a rehacer
   */
  getRedoDescription(): string | null {
    if (this.redoStack.length === 0) return null;
    return this.redoStack[this.redoStack.length - 1].getDescription();
  }

  /**
   * Limpia ambos stacks
   */
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    console.log('[NetworkUndoRedoService] Stacks cleared');
    this.publishState();
  }

  /**
   * Obtiene el estado actual
   */
  getState(): NetworkUndoRedoState {
    return this.snapshot;
  }
}

// Exportar singleton
export const networkUndoRedoService = new NetworkUndoRedoService();
